import { useEffect, useMemo } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
	AppEnum,
	AsyncComboboxAutocomplete,
	NotificationToast, NotificationType, alephToast,
	handleErrorNotification,
} from '@aleph/shared-components';
import debounce from 'debounce-promise';
import { Placeholder } from 'rsuite';
import useSpotifyClientMutator from 'wb-spotify/app/routes/superGrid/mutator/adAccount/hooks/useSpotifyAdClientMutator';
import { useFormik } from 'formik';
import AdAccountAccessListItem from 'wb-spotify/app/routes/superGrid/mutator/adAccount/components/accessListItems';
import { ComboboxAutocompleteOption } from 'wb-reddit/app/common/components/comboboxAutocompleteOption';
import { useTranslation } from 'react-i18next';
import {
	AuthGraphUserGroupsOptionsDocument,
	AuthGraphUserGroupsOptionsQueryItems,
	AuthGraphUserGroupsThatHaveAccessDocument,
} from 'wb-spotify/graphql/authgraph/generated';
import { SpotifyLinkUserGroupsToClientDocument } from 'wb-spotify/graphql/generated';
import useUserMetaData from 'wb-reddit/app/common/hooks/useUserMetaData';
import { IRowNode } from 'ag-grid-community';
import { captureException } from '@sentry/react';

type UpdateAdAccountFormValues = {
	userGroups: AuthGraphUserGroupsOptionsQueryItems[];
};

const UpdateAdAccount = () => {
	const { t } = useTranslation();
	const { mutatingClient, setMutatingClient } = useSpotifyClientMutator();
	const userMetaData = useUserMetaData(AppEnum.SPOTIFY);

	const {
		values,
		setFieldValue,
	} = useFormik<UpdateAdAccountFormValues>({
		initialValues: {
			userGroups: [],
		},
		onSubmit: async () => {
			setMutatingClient(undefined);
		},
	});

	const clientUserGroupIds = useMemo(
		() => (
			mutatingClient?.client?.clientUserGroups
				?.map(group => group?.clientUserGroupGroupId ?? '')
				// Filter out empty (undefined, empty string, null) values
				// since in schema ID is marked as string | undefined
				.filter(Boolean) ?? []
		),
		[mutatingClient?.client?.clientUserGroups],
	);

	const {
		loading: groupsThatHaveAccessLoading,
	} = useQuery(AuthGraphUserGroupsThatHaveAccessDocument, {
		variables:   { selectedGroupIds: clientUserGroupIds },
		onCompleted: (data) => {
			setFieldValue('userGroups', data?.userGroups?.items ?? []);
		},
		skip: !clientUserGroupIds.length,
	});

	useEffect(() => {
		if (mutatingClient?.node) {
			mutatingClient.node?.setData?.(mutatingClient);
			mutatingClient.api?.redrawRows({
				rowNodes: [mutatingClient.node],
			});
		}
	}, [mutatingClient]);

	const [linkGroup, { loading: linkingGroupLoading }] = useMutation(SpotifyLinkUserGroupsToClientDocument, {
		errorPolicy: 'all',
	});

	const onAccessEntitySelected = async (option: AuthGraphUserGroupsOptionsQueryItems) => {
		const { errors: inviteErrors } = await linkGroup({
			variables: {
				clientId:     mutatingClient?.client?.clientId ?? '',
				userGroupIds: [option.userGroupId],
			},
		});

		if (inviteErrors) {
			alephToast(
				<NotificationToast
					type={NotificationType.Error}
					title={t('sc: action err', { action: 'inviting the user' })}
				/>,
			);

			return;
		}

		alephToast(
			<NotificationToast
				type={NotificationType.Success}
				title={<div>Team <strong>{option.label}</strong> has been granted access to <strong>{mutatingClient?.adAccountName}</strong>.</div>}
			/>,
		);

		mutatingClient?.node?.setData?.({
			...mutatingClient,
			client: {
				...mutatingClient?.client,
				clientUserGroups: [
					...(
						mutatingClient?.client?.clientUserGroups ?? []
					),
					{
						clientUserGroupGroupId: option.userGroupId,
					},
				],
			},
		});

		mutatingClient?.api?.redrawRows({
			rowNodes: [mutatingClient?.node as IRowNode<any>],
		});

		setFieldValue('userGroups', [...values.userGroups ?? [], option]);
	};

	const [searchAccessOptions] = useLazyQuery(
		AuthGraphUserGroupsOptionsDocument,
	);

	const debouncedSearchAccessOptions = useMemo(() => debounce(searchAccessOptions, 300), []);

	const loadAccessOptions = async (search: string) => {
		const { data: accessList, error } = await debouncedSearchAccessOptions({
			variables: {
				groupSearch:      `*${search}*`,
				selectedGroupIds: values.userGroups?.map(group => group.userGroupId) ?? [],
			},
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					'sp: ad account access options autocomplete error',
					'Something went wrong while searching for access options',
				),
			);

			return [];
		}

		return (accessList?.userGroups?.items ?? [])
			// Filter out groups that the user doesn't have access to
			.filter(group => group.userGroupAccess?.find(access => access?.user?.userId === userMetaData?.userId));
	};

	return (
		<div
			className="flex flex-1 min-w-full px-4 pb-0 pt-[40px] gap-[40px]"
		>
			<div className="flex flex-1 justify-center w-[25%]" />

			<div className="flex flex-1 flex-col gap-[16px] min-w-[50%] min-h-[100%] px-[2px]">
				<div className="font-semibold text-[14px]">ACCESS</div>

				<AsyncComboboxAutocomplete
					inputProps={{
						placeholder: 'Add teams...',
					}}
					size="large"
					loadOptions={loadAccessOptions}
					disabled={!mutatingClient?.adAccountId}
					renderOption={({ option, searchQuery, onSelected }) => (
						<ComboboxAutocompleteOption
							option={option}
							onOptionClick={onAccessEntitySelected}
							onSelected={onSelected}
							searchQuery={searchQuery}
						/>
					)}
					NoOptionsComponent={() => (
						<div className='max-w-[25vw] w-fit'>
							<strong>No teams found</strong>
						</div>
					)}
				/>

				<div>
					<div className="flex !h-[32px] items-center text-gray-700 px-[8px] py-[18px] text-[12px]
						border-b border-b-gray-200 uppercase"
					>
						<div className="flex-1">On this account only</div>
						<div className="px-[8px]">Domain</div>
						<div className="w-[50px] flex justify-center">Revoke</div>
					</div>

					{
						linkingGroupLoading || groupsThatHaveAccessLoading ? (
							<div className="flex h-[48px] gap-[16px] items-center px-[8px]">
								<Placeholder.Graph
									active
									className="!h-[32px] !w-[32px] min-w-[32px] !rounded-full"
								/>
								<Placeholder.Graph
									active
									className="!h-[16px]"
								/>
							</div>
						) : values.userGroups?.map(group => (
							<AdAccountAccessListItem
								key={group.userGroupId}
								entity={group}
								onRemove={() => {
									mutatingClient?.node?.setData?.({
										...mutatingClient,
										client: {
											...mutatingClient?.client,
											clientUserGroups: (mutatingClient?.client?.clientUserGroups ?? []).filter(
												(item) => item?.clientUserGroupGroupId !== group.userGroupId,
											),
										},
									});
									mutatingClient?.api?.redrawRows({
										rowNodes: [mutatingClient?.node as IRowNode<any>],
									});

									setFieldValue(
										'userGroups',
										(values.userGroups ?? []).filter(g => g.userGroupId !== group.userGroupId),
									);
								}}
							/>
						))
					}

				</div>
			</div>

			<div className="flex flex-1 flex-col w-[25%] gap-[24px]">
				<div className='flex flex-col gap-[16px]'>
					<div className="font-semibold text-[14px]">WISE.BLUE</div>
					<div className="flex text-[12px]">
						<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold">
							<div>ID</div>
						</div>
						<div className="text-black font-normal flex flex-col gap-[8px]">
							<div className="text-gray-800">
								{mutatingClient?.adAccountId ?? '-'}
							</div>
						</div>
					</div>
				</div>

				<div className='flex flex-col gap-[16px]'>
					<div className="font-semibold text-[14px]">SPOTIFY</div>
					<div className="flex text-[12px]">
						<div className="w-[80px] flex flex-col gap-[8px] text-gray-600 font-semibold whitespace-nowrap">
							<div>Website</div>
							<div>Industry</div>
						</div>
						<div className="text-black flex flex-col gap-[8px] whitespace-nowrap">
							<div>
								{
									mutatingClient?.adAccountWebsite ? <a
										href={mutatingClient.adAccountWebsite}
										target="_blank"
										className="text-[12px] text-black font-normal hover:underline
											underline-offset-1 overflow-hidden overflow-ellipsis"
										rel="noreferrer"
									>
										{mutatingClient?.adAccountWebsite}
									</a> : <span className="text-gray-800">-</span>
								}
							</div>
							<div>
								{mutatingClient?.adAccountIndustry}
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UpdateAdAccount;
